import React, { useEffect } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "./store";
import { getCurrentUser } from "./store/slices/authSlice";
import { Toaster } from "react-hot-toast";

// Layouts
import { DashboardLayout } from "./components/common/Layout/DashboardLayout";

// Pages Publiques
import { Login } from "./pages/auth/Login";
import { Register } from "./pages/auth/Register";

// Pages Client
import { Dashboard as ClientDashboard } from "./pages/Client/Dashboard";
import { ClientFactures } from "./pages/Client/ClientFactures";
import { ClientFactureUpload } from "./pages/Client/ClientFactureUpload";
import { ClientFournisseurs } from "./pages/Client/ClientFournisseurs";
import { ClientAnalytics } from "./pages/Client/ClientAnalytics";
import { ClientPaiements } from "./pages/Client/ClientPaiements";

// Pages Admin
import { AdminDashboard } from "./pages/Admin/AdminDashboard";
import { AdminClients } from "./pages/Admin/AdminClients";
import { AdminFactures } from "./pages/Admin/AdminFactures";
import { AdminPaiements } from "./pages/Admin/AdminPaiements";
import { AdminAnalytics } from "./pages/Admin/AdminAnalytics";
import { AdminParametres } from "./pages/Admin/AdminParametres";
import { AdminUtilisateurs } from "./pages/Admin/AdminUtilisateurs";
import { AdminCommissions } from "./pages/Admin/AdminCommissions";
import { AdminBanques } from "./pages/Admin/AdminBanques";
import { AdminLogs } from "./pages/Admin/AdminLogs";
import { AdminFournisseurs } from "./pages/Admin/AdminFournisseurs";

// Notifications
import { NotificationsPage } from "./pages/Notifications/NotificationsPage";

// Pages Fournisseur
import { FournisseurDashboard } from "./pages/Fournisseur/FournisseurDashboard";
import { FournisseurFactures } from "./pages/Fournisseur/FournisseurFactures";
import { FournisseurPaiements } from "./pages/Fournisseur/FournisseurPaiements";
import { FournisseurAnalytics } from "./pages/Fournisseur/FournisseurAnalytics";
import { FournisseurDocuments } from "./pages/Fournisseur/FournisseurDocuments";
import { FournisseurFactureUpload } from "./pages/Fournisseur/FournisseurFactureUpload";
import { FournisseurOnboarding } from "./pages/Fournisseur/FournisseurOnboarding";

const ProtectedRoute: React.FC<{
  children: React.ReactNode;
  allowedRoles?: string[];
}> = ({ children, allowedRoles }) => {
  const { isAuthenticated, user, isLoading } = useSelector(
    (state: RootState) => state.auth
  );
  // Les hooks doivent être appelés avant tout return anticipé (même nombre de hooks à chaque rendu)
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && user && !allowedRoles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }

  if (user && user.role === 'fournisseur' && !user.is_verified) {
    if (location.pathname !== '/fournisseur/onboarding') {
      return <Navigate to="/fournisseur/onboarding" replace />;
    }
  }

  return <>{children}</>;
};

const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

const App: React.FC = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state: RootState) => state.auth);

  useEffect(() => {
    const token = localStorage.getItem("access_token");
    if (token) {
      dispatch(getCurrentUser() as any);
    }
  }, [dispatch]);

  const getDefaultRoute = () => {
    if (!user) return "/login";

    switch (user.role) {
      case "client":
        return "/client/dashboard";
      case "fournisseur":
        return "/fournisseur/dashboard";
      case "admin":
      case "super_admin":
      case "finance":
        return "/admin/dashboard";
      default:
        return "/login";
    }
  };

  return (
    <>
      <Routes>
        {/* Routes publiques */}
        <Route
          path="/login"
          element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          }
        />
        <Route
          path="/register"
          element={
            <PublicRoute>
              <Register />
            </PublicRoute>
          }
        />

        {/* Routes client */}
        <Route
          path="/client"
          element={
            <ProtectedRoute allowedRoles={["client"]}>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route path="dashboard" element={<ClientDashboard />} />
          <Route path="factures" element={<ClientFactures />} />
          <Route path="factures/new" element={<ClientFactureUpload />} />
          <Route path="factures/:id" element={<ClientFactures />} />
          <Route path="fournisseurs" element={<ClientFournisseurs />} />
          <Route path="paiements" element={<ClientPaiements />} />
          <Route path="analytics" element={<ClientAnalytics />} />
        </Route>

        {/* Routes fournisseur */}
        <Route path="/fournisseur/onboarding" element={<ProtectedRoute><FournisseurOnboarding /></ProtectedRoute>} />
        <Route
          path="/fournisseur"
          element={
            <ProtectedRoute allowedRoles={["fournisseur"]}>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route path="dashboard" element={<FournisseurDashboard />} />
          <Route path="factures" element={<FournisseurFactures />} />
          <Route path="factures/new" element={<FournisseurFactureUpload />} />
          <Route path="factures/:id" element={<FournisseurFactures />} />
          <Route path="documents" element={<FournisseurDocuments />} />
          <Route path="paiements" element={<FournisseurPaiements />} />
          <Route path="analytics" element={<FournisseurAnalytics />} />
        </Route>

        {/* Routes admin */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute allowedRoles={["admin", "super_admin", "finance"]}>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route path="dashboard" element={<AdminDashboard />} />
          <Route path="clients" element={<AdminClients />} />
          <Route path="fournisseurs" element={<AdminFournisseurs />} />
          <Route path="factures" element={<AdminFactures />} />
          <Route path="paiements" element={<AdminPaiements />} />
          <Route path="analytics" element={<AdminAnalytics />} />
          <Route path="parametres" element={<AdminParametres />} />
          <Route path="utilisateurs" element={<AdminUtilisateurs />} />
          <Route path="commissions" element={<AdminCommissions />} />
          <Route path="logs" element={<AdminLogs />} />
          <Route path="banques" element={<AdminBanques />} />
        </Route>

        {/* Profil et paramètres du compte — accessible à tous les rôles */}
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<AdminParametres />} />
        </Route>

        {/* Route notifications — accessible à tous les rôles */}
        <Route
          path="/notifications"
          element={
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<NotificationsPage />} />
        </Route>

        {/* Route par défaut */}
        <Route path="/" element={<Navigate to={getDefaultRoute()} replace />} />

        {/* 404 */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      <Toaster
        position="top-right"
        toastOptions={{
          duration: 5000,
          style: {
            background: "#363636",
            color: "#fff",
          },
          success: {
            duration: 3000,
            iconTheme: {
              primary: "#10b981",
              secondary: "#fff",
            },
          },
          error: {
            duration: 5000,
            iconTheme: {
              primary: "#ef4444",
              secondary: "#fff",
            },
          },
        }}
      />
    </>
  );
};

export default App;
